// ──────────────────────────────────────────────────────────────────────
// D62 — Public Calendar Review Page
//
// Mounted at /share/calendar/:token — NO auth. Client opens this link
// from their inbox to review the entire month's content calendar at
// once, click any post for a detail drawer, comment, approve.
// ──────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Loader2, CheckCircle2, MapPin, Calendar, AlertTriangle, MessageCircle, Send,
  ChevronLeft, ChevronRight, Image as ImgIcon, Film,
} from 'lucide-react';
import { publicCalendarShareAPI, publicContentPostAPI } from '../lib/api';
import { toast } from 'sonner';

const PLATFORM_LABELS = { IG: 'Instagram', FB: 'Facebook', TikTok: 'TikTok', X: 'X', YouTube: 'YouTube', Threads: 'Threads', LinkedIn: 'LinkedIn', GMB: 'Google My Business' };
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STATUS_COLORS = {
  idea: 'bg-foreground/[0.05] text-muted-foreground/70 border-border/30',
  draft: 'bg-foreground/[0.07] text-foreground/80 border-border/40',
  in_review: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  scheduled: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  posted: 'bg-gold/15 text-gold border-gold/30',
  failed: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const ymdLocal = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

function buildMonthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - offset);
  const rows = [];
  for (let r = 0; r < 6; r++) {
    const row = [];
    for (let c = 0; c < 7; c++) {
      const d = new Date(start);
      d.setDate(start.getDate() + r * 7 + c);
      row.push(d);
    }
    rows.push(row);
  }
  return rows;
}

export default function PublicCalendarReviewPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activePost, setActivePost] = useState(null);
  const [activePostDetail, setActivePostDetail] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveAllOpen, setApproveAllOpen] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await publicCalendarShareAPI.get(token);
      setData(r.data);
      if (r.data?.share?.recipient_name && !name) setName(r.data.share.recipient_name);
      if (r.data?.share?.recipient_email && !email) setEmail(r.data.share.recipient_email);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to load this calendar. The link may have expired.');
    } finally {
      setLoading(false);
    }
  }, [token, name, email]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  // Parse month
  const monthDate = useMemo(() => {
    if (!data?.share?.month) return new Date();
    const [y, m] = data.share.month.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, 1);
  }, [data?.share?.month]);

  const matrix = useMemo(() => buildMonthMatrix(monthDate.getFullYear(), monthDate.getMonth()), [monthDate]);

  const postsByDate = useMemo(() => {
    const out = {};
    (data?.posts || []).forEach(p => {
      if (!p.scheduled_for) return;
      const key = ymdLocal(new Date(p.scheduled_for));
      (out[key] = out[key] || []).push(p);
    });
    return out;
  }, [data]);

  const openPost = async (p) => {
    setActivePost(p);
    setActivePostDetail(null);
    try {
      const r = await publicContentPostAPI.get(p.share_token);
      setActivePostDetail(r.data);
    } catch (e) {
      toast.error('Unable to load post details');
    }
  };

  const handleAddComment = async () => {
    if (!name.trim() || !email.trim()) { toast.error('Enter your name and email first'); return; }
    if (!commentBody.trim() || !activePost?.share_token) return;
    setPosting(true);
    try {
      await publicContentPostAPI.addComment(activePost.share_token, { name: name.trim(), email: email.trim(), body: commentBody.trim() });
      setCommentBody('');
      toast.success('Comment sent');
      const r = await publicContentPostAPI.get(activePost.share_token);
      setActivePostDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send comment');
    } finally { setPosting(false); }
  };

  const handleApprove = async () => {
    if (!name.trim() || !email.trim() || !activePost?.share_token) { toast.error('Enter your name and email first'); return; }
    setApproving(true);
    try {
      await publicContentPostAPI.approve(activePost.share_token, { name: name.trim(), email: email.trim() });
      toast.success('Post approved — thank you!');
      await load();
      const r = await publicContentPostAPI.get(activePost.share_token);
      setActivePostDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    } finally { setApproving(false); }
  };

  const handleApproveAll = async () => {
    if (!name.trim() || !email.trim()) { toast.error('Enter your name and email first'); return; }
    setApprovingAll(true);
    try {
      const r = await publicCalendarShareAPI.approveAll(token, { name: name.trim(), email: email.trim() });
      toast.success(`Approved ${r.data?.approved_count || 0} posts — thank you!`);
      setApproveAllOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve all');
    } finally { setApprovingAll(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-card border-red-500/30">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-3" />
            <h1 className="text-[16px] font-semibold text-foreground mb-2">Link unavailable</h1>
            <p className="text-[12px] text-muted-foreground/70">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { client, posts } = data;
  const todayKey = ymdLocal(new Date());
  const totalApproved = posts.filter(p => p.client_approved).length;
  const hasUnapproved = posts.length > totalApproved;

  return (
    <div className="min-h-screen bg-background" data-testid="public-calendar-review">
      <header className="border-b border-border/20 bg-card/40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] text-gold uppercase tracking-wider">Stellar Productions</div>
            <h1 className="font-display text-[16px] sm:text-[18px] font-semibold text-foreground">Content Calendar · {client?.name} · {MONTH_NAMES[monthDate.getMonth()]} {monthDate.getFullYear()}</h1>
          </div>
          <div className="flex items-center gap-2">
            {posts.length > 0 && (
              <span className="text-[11px] text-muted-foreground/70" data-testid="public-cal-summary">{totalApproved}/{posts.length} approved</span>
            )}
            {hasUnapproved && (
              <Button onClick={() => setApproveAllOpen(true)} className="h-8 text-[12px] bg-emerald-500 hover:bg-emerald-600 text-white border-0" data-testid="public-approve-all-btn">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve all
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-3">
        {/* Reviewer identity (sticky-top hint) */}
        <Card className="bg-card border-border/30">
          <CardContent className="p-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground/60">Your name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" data-testid="public-cal-reviewer-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground/60">Your email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" data-testid="public-cal-reviewer-email" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Month grid (matches deadline-calendar styling) */}
        <Card className="card-hover-gold"><CardContent className="p-2 overflow-x-auto">
          <div className="min-w-[640px] sm:min-w-0">
            <div className="grid grid-cols-7 mb-1">
              {DAY_LABELS.map(d => <div key={d} className="text-center text-[11px] font-semibold text-muted-foreground/50 py-2">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="public-cal-grid">
              {matrix.flat().map((d, idx) => {
                const inMonth = d.getMonth() === monthDate.getMonth();
                const key = ymdLocal(d);
                const dayPosts = postsByDate[key] || [];
                const isToday = key === todayKey;
                const dow = idx % 7;
                const isWeekend = dow === 5 || dow === 6;
                const cellBg = isToday
                  ? 'border-gold/40 bg-gold/[0.06] ring-1 ring-gold/20'
                  : isWeekend
                    ? 'border-transparent bg-foreground/[0.025]'
                    : 'border-transparent bg-foreground/[0.01]';
                return (
                  <div key={idx} className={`min-h-[100px] rounded-lg p-1 border ${cellBg} ${inMonth ? '' : 'opacity-50'}`}>
                    <div className="flex items-center justify-between px-0.5">
                      <span className={`text-[11px] font-semibold ${isToday ? 'text-gold' : isWeekend && inMonth ? 'text-muted-foreground/60' : inMonth ? 'text-foreground/70' : 'text-muted-foreground/30'}`}>{d.getDate()}</span>
                      {isToday && <span className="text-[8px] text-gold/70 font-semibold">TODAY</span>}
                    </div>
                    <div className="mt-0.5 space-y-0.5">
                      {dayPosts.map(p => (
                        <button
                          key={p.id}
                          onClick={() => openPost(p)}
                          className={`flex items-center gap-1.5 w-full p-1 rounded border-l-2 text-left hover:bg-foreground/[0.06] transition-all ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}
                          data-testid={`public-cal-chip-${p.id}`}
                          title={p.caption || '(no caption)'}
                        >
                          {p.media?.[0]?.kind === 'video' ? <Film className="h-2.5 w-2.5 shrink-0" /> : p.media?.[0] ? <ImgIcon className="h-2.5 w-2.5 shrink-0" /> : <span className="h-1.5 w-1.5 rounded-full bg-current shrink-0 opacity-60" />}
                          <span className="text-[11px] font-medium truncate flex-1">{p.caption || '(no caption)'}</span>
                          {p.client_approved && <CheckCircle2 className="h-2.5 w-2.5 text-emerald-400 shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent></Card>

        {posts.length === 0 && (
          <div className="text-center py-8 text-[12px] text-muted-foreground/60" data-testid="public-cal-empty">No posts scheduled for this month yet.</div>
        )}

        <footer className="text-center text-[10px] text-muted-foreground/40 pt-4 pb-8">
          Powered by Stellar Productions CRM
        </footer>
      </main>

      {/* Post detail drawer */}
      <Dialog open={!!activePost} onOpenChange={(o) => { if (!o) { setActivePost(null); setActivePostDetail(null); } }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="public-cal-post-drawer">
          <DialogHeader>
            <DialogTitle className="font-display text-[16px] flex items-center gap-2">
              Post preview
              {activePostDetail?.post?.client_approved && (
                <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Approved</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {!activePostDetail ? (
            <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-gold inline" /></div>
          ) : (
            <div className="space-y-3 py-2">
              {(activePostDetail.post.media || []).length > 0 && (
                <div className="grid grid-cols-2 gap-1">
                  {activePostDetail.post.media.map(m => (
                    <div key={m.id} className="aspect-square rounded overflow-hidden bg-foreground/[0.05]">
                      {m.kind === 'video' ? <video src={m.url} poster={m.thumbnail_url || undefined} controls preload="metadata" className="w-full h-full object-cover bg-black" /> : <img src={m.url} alt={m.filename || ''} loading="lazy" className="w-full h-full object-cover" />}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground/70">
                {activePostDetail.post.scheduled_for && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(activePostDetail.post.scheduled_for).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
                {(activePostDetail.post.platforms || []).map(p => <span key={p} className="px-2 py-0.5 rounded bg-gold/10 text-gold border border-gold/20">{PLATFORM_LABELS[p] || p}</span>)}
                {(activePostDetail.post.target_locations || []).length > 0 && (
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {activePostDetail.post.target_locations.map(l => l.location_name).join(', ')}</span>
                )}
              </div>
              <div className="whitespace-pre-wrap text-[13px] text-foreground/90" data-testid="public-cal-post-caption">{activePostDetail.post.caption || '(no caption)'}</div>

              {!activePostDetail.post.client_approved && (
                <Button onClick={handleApprove} disabled={approving || !name || !email} className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-white border-0" data-testid="public-cal-approve-btn">
                  {approving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Approve this post
                </Button>
              )}

              <div className="border-t border-border/20 pt-3">
                <div className="text-[12px] font-medium text-foreground/80 flex items-center gap-1.5 mb-2"><MessageCircle className="h-3.5 w-3.5" /> Comments ({(activePostDetail.comments || []).length})</div>
                <div className="space-y-2 max-h-[200px] overflow-y-auto mb-2">
                  {(activePostDetail.comments || []).length === 0 ? (
                    <div className="text-[11px] text-muted-foreground/50 italic">No comments yet.</div>
                  ) : activePostDetail.comments.map(c => (
                    <div key={c.id} className="text-[12px] bg-foreground/[0.03] border border-border/20 rounded p-2">
                      <div className="flex items-center gap-1.5 mb-0.5 text-[10px]">
                        <span className={`px-1.5 py-0.5 rounded ${c.author_type === 'external_client' ? 'bg-blue-500/15 text-blue-300' : 'bg-gold/15 text-gold'}`}>
                          {c.author_type === 'external_client' ? 'Client' : 'Stellar team'}
                        </span>
                        <span className="font-medium text-foreground/90">{c.author_name}</span>
                        <span className="text-muted-foreground/40">{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-foreground/80 whitespace-pre-wrap">{c.body}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <Textarea value={commentBody} onChange={e => setCommentBody(e.target.value)} rows={2} placeholder="Add a comment…" className="text-[12px]" data-testid="public-cal-comment-input" />
                  <Button onClick={handleAddComment} disabled={posting || !commentBody.trim()} className="h-auto self-stretch px-3 bg-gold/15 hover:bg-gold/25 text-gold border-0" data-testid="public-cal-comment-send">
                    {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setActivePost(null); setActivePostDetail(null); }} className="text-[12px] h-8 border-border/30">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve all confirm */}
      <Dialog open={approveAllOpen} onOpenChange={(o) => { if (!approvingAll) setApproveAllOpen(o); }}>
        <DialogContent className="sm:max-w-[440px]" data-testid="public-approve-all-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-[16px]">Approve all unapproved posts?</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground/70 py-2">This will approve every post in this month that hasn't been approved yet. The Stellar team will be notified.</p>
          {(!name || !email) && (
            <p className="text-[11px] text-amber-400 py-1">Please enter your name and email above first.</p>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setApproveAllOpen(false)} disabled={approvingAll} className="text-[12px] h-8 border-border/30">Cancel</Button>
            <Button size="sm" onClick={handleApproveAll} disabled={approvingAll || !name || !email} className="text-[12px] h-8 bg-emerald-500 hover:bg-emerald-600 text-white border-0" data-testid="public-approve-all-confirm">
              {approvingAll ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1.5" />}
              Approve all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
